// Keep the JSON payload as a string so each example preserves the same values.
const phpString = ( value ) => "'" + value.replace( /\\/g, '\\\\' ).replace( /'/g, "\\'" ) + "'";
const shellString = ( value ) => "'" + value.replace( /'/g, "'\"'\"'" ) + "'";
const csharpString = ( value ) => '@"' + value.replace( /"/g, '""' ) + '"';

export function generateApiExamples( endpoint, templateId, variables )
{
    const payload = JSON.stringify( { templateId, variables }, null, 2 );

    return [
        {
            id: 'curl',
            label: 'cURL',
            code: `# Run in a POSIX shell (bash or zsh).
curl --fail-with-body --output output.pdf \\
  --request POST ${shellString( endpoint )} \\
  --header 'x-apitoken: YOUR_API_TOKEN' \\
  --form-string ${shellString( 'data=' + payload )}`
        },
        {
            id: 'php',
            label: 'PHP',
            code: `<?php
// Requires the PHP cURL extension.
$apiToken = 'YOUR_API_TOKEN';
$endpoint = ${phpString( endpoint )};
$data = ${phpString( payload )};

$ch = curl_init($endpoint);
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ['x-apitoken: ' . $apiToken],
    CURLOPT_POSTFIELDS => ['data' => $data],
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
$error = curl_error($ch);
curl_close($ch);

if ($response === false) {
    throw new RuntimeException($error);
}
if ($httpCode !== 201) {
    throw new RuntimeException('HTTP ' . $httpCode . ': ' . $response);
}
file_put_contents('output.pdf', $response);
echo "Document saved to output.pdf\\n";`
        },
        {
            id: 'nodejs',
            label: 'Node.js',
            code: `// Node.js 22+. Save as generate.cjs and run: node generate.cjs
const { writeFile } = require('node:fs/promises');

async function main() {
    const apiToken = 'YOUR_API_TOKEN';
    const endpoint = ${JSON.stringify( endpoint )};
    const data = ${JSON.stringify( payload )};
    const form = new FormData();
    form.append('data', data);

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'x-apitoken': apiToken },
        body: form,
    });
    if (response.status !== 201) {
        throw new Error('HTTP ' + response.status + ': ' + await response.text());
    }
    await writeFile('output.pdf', Buffer.from(await response.arrayBuffer()));
    console.log('Document saved to output.pdf');
}

main().catch(error => {
    console.error(error);
    process.exitCode = 1;
});`
        },
        {
            id: 'python',
            label: 'Python',
            code: `# Install the HTTP client: python -m pip install requests
import requests

api_token = 'YOUR_API_TOKEN'
endpoint = ${JSON.stringify( endpoint )}
data = ${JSON.stringify( payload )}

response = requests.post(
    endpoint,
    headers={'x-apitoken': api_token},
    files={'data': (None, data)},
    timeout=120,
)
if response.status_code != 201:
    raise RuntimeError(f'HTTP {response.status_code}: {response.text}')

with open('output.pdf', 'wb') as output:
    output.write(response.content)
print('Document saved to output.pdf')`
        },
        {
            id: 'dotnet',
            label: '.NET',
            code: `// Paste into Program.cs in a .NET 8+ console project.
using System;
using System.IO;
using System.Net.Http;

var apiToken = "YOUR_API_TOKEN";
var endpoint = ${csharpString( endpoint )};
var payload = ${csharpString( payload )};

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("x-apitoken", apiToken);
using var form = new MultipartFormDataContent();
form.Add(new StringContent(payload), "data");
using var response = await client.PostAsync(endpoint, form);

if (response.StatusCode != System.Net.HttpStatusCode.Created) {
    throw new HttpRequestException($"HTTP {(int)response.StatusCode}: {await response.Content.ReadAsStringAsync()}");
}
await File.WriteAllBytesAsync("output.pdf", await response.Content.ReadAsByteArrayAsync());
Console.WriteLine("Document saved to output.pdf");`
        }
    ];
}
